import { useEffect } from "react";
import * as React from "react"; 
import { useAppDispatch, useAppSelector } from "../../hooks";
import {
    DataTable,
    DataTableBody,
    DataTableCell,
    DataTableColumnHeaderCell,
    DataTableContent,
    DataTableHeaderRow,
    DataTablePagination,
    DataTableRow,
    DataTableToolbar,
    TextButton,
} from "@episerver/ui-framework";
import {
    onChangeChildOrganizationPage,
    onChangeChildOrganizationRowPerPage,
    childOrganizationListPaginationSelector,
} from "../reducers/childOrganizationListReducer";
import { fetchChildOrganizations } from "../reducers/asyncThunks";
import { Organization } from "../model/organization";
import { useHistory } from "react-router-dom";
import { onChangeDialog } from "../reducers/addChildOrganizationsReducer";
import { clearAddChildError } from "../reducers/addChildOrganizationsReducer";
import { organizationSelector } from "../reducers/organizationDetailReducer";
import { useSelector } from "react-redux";
import { readOnlySelector } from "../../AdminInfo/adminInfoReducer";

interface ChildOrganizationProps {
    parentOrganizationId: string;
}

const ChildOrganizationList = (props: ChildOrganizationProps) => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const organization = useAppSelector(organizationSelector);
    const { parentOrganizationId } = props;
    const { rowsPerPage, startRow, rowsPerPageOptions, totalRowCount } = useAppSelector(
        childOrganizationListPaginationSelector
    );
    const readOnly = useSelector(readOnlySelector);

    useEffect(() => {
        dispatch(fetchChildOrganizations(parentOrganizationId));
    }, []);

    const columns: Array<{ selector: string; title: string; sortable: boolean }> = [
        { selector: "name", title: "Name", sortable: true },
        { selector: "organizationType", title: "Type", sortable: true },
        { selector: "payerId", title: "Payer Id", sortable: true },
        { selector: "onlineRegistrationCode", title: "Online Registration Code", sortable: true },
        { selector: "masterPayerId", title: "Master Payer Id", sortable: true },
    ];

    const renderRow = (rowData: Organization): React.ReactElement => {
        return (
            <DataTableRow rowId={rowData.primaryKeyId!} key={rowData.primaryKeyId}>
                <DataTableCell>
                    <a
                        href=""
                        className="underline-link"
                        onClick={(e) => {
                            e.preventDefault();
                            history.push("/Organizations/Edit/" + rowData.primaryKeyId);
                        }}
                    >
                        {rowData.name}
                    </a>
                </DataTableCell>
                <DataTableCell>{rowData.organizationType ?? ""}</DataTableCell>
                <DataTableCell>{rowData.extendedProperties?.find(property => property.name === "PayerId")?.value ?? ""}</DataTableCell>
                <DataTableCell>{rowData.extendedProperties?.find(property => property.name === "OnlineRegistrationCode")?.value ?? ""}</DataTableCell>
                <DataTableCell>{rowData.extendedProperties?.find(property => property.name === "MasterPayerId")?.value ?? ""}</DataTableCell>
            </DataTableRow>
        );
    };

    return (
        <div className="child-organization-list">
            <DataTable>
                <DataTableToolbar className="child-organization-list__toolbar">
                    <div>
                        {!readOnly &&
                            <TextButton
                                onClick={() => {
                                    dispatch(
                                        onChangeDialog({
                                            open: true,
                                        })
                                    );
                                    dispatch(clearAddChildError());
                                }}
                            >
                                Add
                            </TextButton>
                        }
                    </div>
                </DataTableToolbar>
                <DataTableContent>
                    <DataTableHeaderRow>
                        <React.Fragment>
                            {columns.map((column) => {
                                return (
                                    <DataTableColumnHeaderCell key={column.selector}>
                                        {column.title}
                                    </DataTableColumnHeaderCell>
                                );
                            })}
                        </React.Fragment>
                    </DataTableHeaderRow>
                    <DataTableBody>
                        {organization.children.map((row: Organization, index: number) => {
                            if (index < startRow || index >= startRow + rowsPerPage) {
                                return <React.Fragment key={row.primaryKeyId}></React.Fragment>;
                            }
                            return renderRow(row);
                        })}
                    </DataTableBody>
                </DataTableContent>
                <DataTablePagination
                    startRow={startRow}
                    totalRowCount={totalRowCount}
                    rowsPerPage={rowsPerPage}
                    rowsPerPageOptions={rowsPerPageOptions}
                    onChangePage={(startRow) => dispatch(onChangeChildOrganizationPage(startRow))}
                    onChangeRowsPerPage={(rowsPerPage) => dispatch(onChangeChildOrganizationRowPerPage(rowsPerPage))}
                />
            </DataTable>
        </div>
    );
};

export default ChildOrganizationList;
